import {Injectable, OnDestroy} from '@angular/core';
import {ActivityService} from '@docma-mm-core/activity/activity.service';

import {AppContainerOptions, AppRightPaneService, RightPaneContent} from '@mydocma/app-container';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {FaultViewEnum, MmFaultViewService} from '../../mm-fault/mm-fault-view.service';
import {MmFaultRightPanePageEnum} from './mm-fault-right-pane.enum';
import {IPanePage} from './pages/pane-page';
import {MmTranslateService} from '../../../mm-translate.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {ScreenOrientationEnum, ScreenOrientationService} from '@mydocma/core';
import {Logger} from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Injectable()
export class MmFaultRightPaneService implements OnDestroy {

  pagesPath: string[] = [];

  private savingSubject = new BehaviorSubject<boolean>(false);
  saving$ = this.savingSubject.asObservable();

  private donePressedSubject = new BehaviorSubject<boolean>(false);
  donePressed$ = this.donePressedSubject.asObservable();

  private isUndockedSubject = new BehaviorSubject<boolean>(false);
  isUndocked$ = this.isUndockedSubject.asObservable();

  private currentPageSubject = new BehaviorSubject<MmFaultRightPanePageEnum>(null);
  public currentPage$ = this.currentPageSubject.asObservable();

  private faultDetailsService: any = null;

  private discardPromptInFlight: Promise<boolean> = null;


  constructor(
    private readonly appContainer: AppContainerOptions,
    private viewService: MmFaultViewService,
    private readonly activityService: ActivityService,
    private readonly translateService: MmTranslateService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: PromptDialogService,
    private readonly appRightPaneService: AppRightPaneService,
    private readonly deviceService: DeviceService,
    private readonly screenOrientationService: ScreenOrientationService,
    private readonly logger: Logger
  ) {

    this.viewService.onToggleViewRequested
      .pipe(untilDestroyed(this))
      .subscribe(request => {
        try {
          switch (request.view) {

            // case FaultViewEnum.Fault:
            //   this.setHeaderFromFault(this.fault);
            //   this.view = FaultViewEnum.Fault;
            //   break;
            // case FaultViewEnum.NewFaultAttachmentFromCamera:
            //   this.createAttachmentFromCamera(data);
            //   break;
            // case FaultViewEnum.NewFaultAttachmentFromFile:
            //   this.createAttachmentFromFile(data);
            //   break;

            case FaultViewEnum.EditFault:
              this.openEditFaultPage(request.data);
              break;
            case FaultViewEnum.NewFaultAttachmentFromFile:
              this.openNewAttachmentFromFilePage(request.data);
              break;
            case FaultViewEnum.NewActivityAttachmentFromFile:
              this.openNewAttachmentFromFilePage(request.data);
              break;
            case FaultViewEnum.NewActivity:
              this.openNewActivityPage(request.data);
              break;
            case FaultViewEnum.EditActivity:
              this.openEditActivityPage(request.data);
              break;
            case FaultViewEnum.SelectFaultStructure:
              this.openNextPanePage(MmFaultRightPanePageEnum.selectStructure, request.data);
              break;
            case FaultViewEnum.ConfigFields:
              this.openConfigFields(request.data);

            default:
              break;
          }

        } catch (err) {
          this.logger.error(err, '', 'MmFaultRightPaneService');
          this.notificationService.error(err);
        }
      });

  }

  ngOnDestroy(): void {
  }

  public get isPaneUndocked(): boolean {
    return this.isUndockedSubject.value;
  }

  get currentPage() {
    return this.currentPageSubject.value;
  }

  public setFaultDetailsService(service: any) {
    this.faultDetailsService = service;
  }

  async openFirstPanePage(page: MmFaultRightPanePageEnum, data?: any, undocked?: boolean) {
    this.clear();
    this.openNextPanePage(page, data, undocked);
  }

  async openNextPanePage(page: MmFaultRightPanePageEnum, data?: any, undocked?: boolean) {
    undocked = undocked ?? this.deviceService.isTablet();
    this.currentPageSubject.next(page);
    if (this.appRightPaneService.visibleRightPaneContentId !== page || (undocked && !this.isUndockedSubject.value)) {
      this.appRightPaneService.visibleRightPaneContentId = page;
      this.appRightPaneService.createRightPaneComponentIfNecessary();
      if (!this.appRightPaneService.opened || (undocked && !this.appRightPaneService.dockState?.undocked)) {
        this.isUndockedSubject.next(undocked);
        this.appRightPaneService.open({undocked,
          dialogOptions: {
            panelClass: ['mydocma-mm-fault-pane-dialog', undocked ? 'undocked' : '', undocked ? 'mm-keyboard-fit-modal' : ''],
            height: (this.deviceService.isTablet() && await firstValueFrom(this.screenOrientationService.currentScreenOrientation$) == ScreenOrientationEnum.portrait) ? 'calc(100vh - 280px)' : 'calc(100vh - 104px)',
            width: '750px',
            autoFocus: 'dialog',
            restoreFocus: !undocked
          } as any
        });
      }
    }

    this.setPageData(page, data);
    this.pagesPath.push(page);
  }

  async saveChanges(): Promise<boolean> {

    const isOpen = await firstValueFrom(this.appRightPaneService.isOpen);
    if (!this.pagesPath || this.pagesPath.length === 0 || !isOpen) {
      return true;
    }

    const lastPage = this.getLastPanePage();

    if (!lastPage?.hasChanges()) {
      return true;
    }
    if (!(this.getLastPageContent().id === MmFaultRightPanePageEnum.fault || this.getLastPageContent().id === MmFaultRightPanePageEnum.editFault)) {
      return await this.promtDiscardChanges();
    }
    try {
      this.savingSubject.next(true);
      if (await lastPage.save()) {
        this.savingSubject.next(false);
        return true;
      }
    } catch (err) {
      this.logger.error(err, '', 'MmFaultRightPaneService');
      this.notificationService.error(err);
    }
    this.savingSubject.next(false);
    return false;
  }

  async closePanePage(performSaving?: boolean, confirmDiscarding?: boolean, skipDialogDuringInit?: boolean) {

    let closePage = true;

    if (!this.pagesPath || this.pagesPath.length === 0) {
      return true;
    }
    const lastContent = this.getLastPageContent();
    const lastPage = this.getLastPanePage();

    try {
      if (performSaving) {
        if (!await this.saveLastPage()) {
          return;
        }
      } else if (confirmDiscarding) {
        if (this.savingSubject.value) {
          return true;
        }

        if (skipDialogDuringInit) {
          console.warn('[closePanePage] SKIPPING dialog - skipDialogDuringInit=true (create from plan initialization)');
        } else if (lastPage?.hasChanges()) {
          if (!this.faultDetailsService?.shouldFaultBeHiddenFromView()) {
            closePage = await this.promtDiscardChanges();
          }
          if (!closePage) {
            return closePage;
          } else {
            await lastPage.discardChanges();
            await lastPage.clearData();
          }
        }
      }

    } catch (error) {
      this.logger.error(error, '', 'MmFaultRightPaneService');
      this.notificationService.error(error);
      return;
    }

    if (this.pagesPath.length < 2) {
      await this.closePane();
    }
    this.pagesPath.splice(this.pagesPath.length - 1, 1);
    const page = this.pagesPath[this.pagesPath.length - 1];
    this.appRightPaneService.visibleRightPaneContentId = page;
    this.currentPageSubject.next(page as MmFaultRightPanePageEnum);

    if (closePage) {
      await lastPage?.clearData();
    }

    return closePage;
  }

  async closePane() {
    this.appRightPaneService.close();
  }

  async promtDiscardChanges(): Promise<boolean> {
    if (this.discardPromptInFlight) {
      return this.discardPromptInFlight;
    }
    this.discardPromptInFlight = (async () => {
      try {
        const title = await this.translateService.get('MM.dialog.discardChanges.title').toPromise();
        const content = await this.translateService.get('MM.dialog.discardChanges.content').toPromise();
        return (await this.dialogService.showDiscardChangesConfirmationDialog({title, content}))?.value;
      } finally {
        this.discardPromptInFlight = null;
      }
    })();
    return this.discardPromptInFlight;
  }

  private openNewAttachmentFromFilePage(data: any) {
    // const attachment = new Attachment({changeState: new ChangeState({status: ChangeStatusEnum.New})});
    // data.attachment = attachment;
    this.openNextPanePage(MmFaultRightPanePageEnum.addAttachments, data);
  }

  private openEditFaultPage(data: any) {
    this.openFirstPanePage(MmFaultRightPanePageEnum.editFault, data);
  }

  private openNewActivityPage(data: any) {
    if (data.faults.length > 1) {
      this.openFirstPanePage(MmFaultRightPanePageEnum.addActivity, data);
    } else {
      this.openNextPanePage(MmFaultRightPanePageEnum.addActivity, data);
    }
  }

  private openEditActivityPage(data: any) {
    this.openNextPanePage(MmFaultRightPanePageEnum.editActivity, data);
  }

  private openConfigFields(data: any) {
    this.openNextPanePage(MmFaultRightPanePageEnum.fieldConfiguration, data);
  }

  private clear() {
    this.pagesPath = [];
  }

  private getPaneContent(page: string): RightPaneContent {
    return this.appRightPaneService.rightPaneCache.find(c => c.id === page);
  }

  private setPageData(page: MmFaultRightPanePageEnum, data?: any) {
    const content = this.getPaneContent(page);
    if (content?.componentRef && data) {
      const panePage = (content.componentRef.instance as IPanePage);
      if (panePage.setData) {
        panePage.setData(data);
      }
    }
  }

  private getLastPageContent(): RightPaneContent {
    const page = this.pagesPath[this.pagesPath.length - 1];
    return this.getPaneContent(page);
  }

  private getLastPanePage(): IPanePage {
    const content = this.getLastPageContent();
    return (content?.componentRef?.instance as IPanePage);
  }

  private async saveLastPage(): Promise<boolean> {
    let result: boolean;
    try {
      this.savingSubject.next(true);
      result = await this.getLastPanePage().save();
      this.savingSubject.next(false);
    } catch (error) {
      this.savingSubject.next(false);
      throw error;
    }
    return result;
  }

  public async withDonePressed<T>(action: () => Promise<T>): Promise<T> {
    this.donePressedSubject.next(true);

    try {
      return await action();
    } finally {
      this.donePressedSubject.next(false);
    }
  }

}
